import { FC, useMemo, useRef, useState } from "react";
import { Box, Stack, Typography } from "@mui/material";
import { FilterConditionToolbar, TSavedCondition as TToolbarSavedCondition } from "@irohana-dev/components";
import { useDeleteSavedCondition, useSavedConditionList } from "@/api/services/main/saved-condition";
import { ConfirmDeleteModal } from "@/components/commons/ConfirmDeleteModal";
import { PastJobsFilterConditionPanel } from "@/features/company/components/company-detail/past-jobs/PastJobsFilterConditionPanel";
import { applyColumnSettings } from "@/features/company/components/company-detail/past-jobs/past-jobs-filter-condition-adapter";
import {
  TPastJobsFilterForm,
  TJobDisplayColumnSetting,
  defaultPastJobsDisplayColumns,
  defaultPastJobsFilterValues,
  getPastJobsDisplayColumnsKey,
  getPastJobsPageType,
  isEmptyPastJobsFilterValue,
  normalizePastJobsDateFields,
  normalizePastJobsMultiSelectFields,
} from "@/features/company/components/company-detail/past-jobs/past-jobs-shared";
import { showError } from "@/utils/error";
import { toast } from "@/utils/toast";
import { filterConditionShareColor } from "@/components/commons/filter-condition/filter-condition-share-color";
import { hasDisplayColumnsChanged } from "@/components/commons/filter-condition/filter-condition-adapter-kit";

/** The 「デ」 badge uses the project primary for both scopes, on its lightest tint. */

type Props = {
  total: number;
  page: number;
  perPage: number;
  onApplyAdvancedFilters?: (filters: TPastJobsFilterForm) => void;
  onApplyDisplaySettings?: (columns: TJobDisplayColumnSetting[]) => void;
  appliedConditionName?: string;
  appliedConditionId?: number | null;
  onAppliedConditionNameChange?: (name: string) => void;
  onAppliedConditionIdChange?: (id: number | null) => void;
  currentDisplayColumns?: TJobDisplayColumnSetting[] | null;
  currentAdvancedFilters?: TPastJobsFilterForm | null;
  companyId?: number;
};

export const PastJobsTop: FC<Props> = ({
  total,
  page,
  perPage,
  onApplyAdvancedFilters,
  onApplyDisplaySettings,
  appliedConditionName,
  appliedConditionId,
  onAppliedConditionNameChange,
  onAppliedConditionIdChange,
  currentDisplayColumns,
  currentAdvancedFilters,
  companyId,
}) => {
  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLElement | null>(null);
  const [filterMode, setFilterMode] = useState<"create" | "edit">("create");
  const [isClearConfirmOpen, setIsClearConfirmOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const { data: savedConditionsData } = useSavedConditionList(getPastJobsPageType(companyId));
  const deleteSavedCondition = useDeleteSavedCondition();

  const handleOpenCreateFilter = () => {
    setFilterMode("create");
    setFilterAnchorEl(document.body);
  };

  const handleOpenEditFilter = () => {
    setFilterMode("edit");
    setFilterAnchorEl(document.body);
  };

  const justSavedConditionRef = useRef(false);

  const handleApplyFiltersFromPanel = (filters: TPastJobsFilterForm) => {
    onApplyAdvancedFilters?.(filters);
    // A save applies its own condition right after this, so the create-mode reset would
    // wipe the id/name that were just set.
    if (justSavedConditionRef.current) {
      justSavedConditionRef.current = false;
      return;
    }
    if (filterMode === "create") {
      onAppliedConditionNameChange?.("");
      onAppliedConditionIdChange?.(null);
    }
  };

  const handleConditionSaved = (id: number, title: string) => {
    justSavedConditionRef.current = true;
    onAppliedConditionNameChange?.(title);
    onAppliedConditionIdChange?.(id);
    setFilterMode("edit");
  };

  const appliedCondition =
    filterMode === "edit" && appliedConditionId
      ? savedConditionsData?.data.find((item) => Number(item.id) === Number(appliedConditionId))
      : undefined;

  const activeFilterCount = useMemo(() => {
    if (!currentAdvancedFilters) return 0;
    const { endDateFrom, endDateTo, ...rest } = currentAdvancedFilters;
    let count = Object.values(rest).filter((v) => !isEmptyPastJobsFilterValue(v)).length;
    if (endDateFrom || endDateTo) count += 1;
    return count;
  }, [currentAdvancedFilters]);

  const savedConditions = useMemo<TToolbarSavedCondition[]>(
    () =>
      (savedConditionsData?.data ?? []).map((item) => ({
        id: Number(item.id),
        name: item.title,
        shareScope: item.shareScope ?? "self",
        isDefault: item.isDefault ?? false,
        badgeColor: item.defaultColor ?? undefined,
      })),
    [savedConditionsData],
  );

  // A condition that stored 項目設定 owns the columns too, so picking it restores them.
  // One that did not keeps whatever the user currently has.
  const applySavedColumnSettings = (picked: { includeColumnSettings?: boolean; columnSettings?: unknown }) => {
    if (!picked.includeColumnSettings || !picked.columnSettings) return;
    const settings = picked.columnSettings as { order?: string[]; hidden?: string[] };
    const base = currentDisplayColumns ?? [...defaultPastJobsDisplayColumns];
    const next = applyColumnSettings(base, settings.order ?? [], settings.hidden ?? []);
    onApplyDisplaySettings?.(next);
    localStorage.setItem(getPastJobsDisplayColumnsKey(companyId), JSON.stringify(next));
  };

  const handleSelectCondition = (id: string | number) => {
    const picked = savedConditionsData?.data.find((item) => Number(item.id) === Number(id));
    if (!picked) return;
    onApplyAdvancedFilters?.({
      ...defaultPastJobsFilterValues,
      ...normalizePastJobsMultiSelectFields(normalizePastJobsDateFields(picked.filters ?? {}) as Partial<Record<string, unknown>>),
    } as TPastJobsFilterForm);
    applySavedColumnSettings(picked);
    onAppliedConditionNameChange?.(picked.title);
    onAppliedConditionIdChange?.(Number(picked.id));
  };

  const handleDeleteAppliedCondition = async () => {
    if (!appliedConditionId) return;
    try {
      await deleteSavedCondition.mutateAsync(appliedConditionId);
      toast.success("条件を削除しました。");
      onApplyAdvancedFilters?.({ ...defaultPastJobsFilterValues });
      onAppliedConditionNameChange?.("");
      onAppliedConditionIdChange?.(null);
    } catch (e) {
      showError(e);
    } finally {
      setIsDeleteConfirmOpen(false);
    }
  };

  const handleClearAppliedFilter = () => {
    onApplyAdvancedFilters?.(defaultPastJobsFilterValues);
    onAppliedConditionNameChange?.("");
    onAppliedConditionIdChange?.(null);
    onApplyDisplaySettings?.([...defaultPastJobsDisplayColumns]);
    localStorage.setItem(getPastJobsDisplayColumnsKey(companyId), JSON.stringify(defaultPastJobsDisplayColumns));
    setIsClearConfirmOpen(false);
  };

  return (
    <>
      <Box sx={{ mb: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="body14Semi">
            全 {total} 件中 {Math.min(page * perPage, total)} 件を表示
          </Typography>

          <FilterConditionToolbar
            onOpenFilter={handleOpenCreateFilter}
            activeCount={
              activeFilterCount || Number(Boolean(appliedConditionId) || hasDisplayColumnsChanged(currentDisplayColumns, defaultPastJobsDisplayColumns))
            }
            conditions={savedConditions}
            selectedId={appliedConditionId ?? undefined}
            onSelect={handleSelectCondition}
            onClearSelection={() => setIsClearConfirmOpen(true)}
            onEdit={handleOpenEditFilter}
            onDelete={() => setIsDeleteConfirmOpen(true)}
            shareColor={filterConditionShareColor}
          />
        </Stack>
      </Box>

      <PastJobsFilterConditionPanel
        mode={filterMode}
        companyId={companyId}
        appliedConditionId={filterMode === "edit" ? appliedConditionId : null}
        appliedConditionName={filterMode === "edit" ? appliedConditionName : ""}
        isOpen={Boolean(filterAnchorEl)}
        onClose={() => setFilterAnchorEl(null)}
        onApplyFilters={handleApplyFiltersFromPanel}
        onApplyDisplaySettings={onApplyDisplaySettings}
        initialDisplayColumns={currentDisplayColumns || undefined}
        initialFilters={filterMode === "edit" ? currentAdvancedFilters || undefined : defaultPastJobsFilterValues}
        appliedConditionShareScope={appliedCondition?.shareScope}
        appliedConditionIsDefault={appliedCondition?.isDefault}
        appliedConditionIncludeColumnSettings={appliedCondition?.includeColumnSettings}
        appliedConditionColumnSettings={appliedCondition?.columnSettings as { order?: string[]; hidden?: string[] } | null | undefined}
        onConditionSaved={handleConditionSaved}
      />
      <ConfirmDeleteModal
        open={isDeleteConfirmOpen}
        onClose={() => setIsDeleteConfirmOpen(false)}
        onOk={handleDeleteAppliedCondition}
        message="この条件を削除してもよろしいですか？"
        title=""
      />
      <ConfirmDeleteModal
        open={isClearConfirmOpen}
        onClose={() => setIsClearConfirmOpen(false)}
        onOk={handleClearAppliedFilter}
        message="設定した条件がすべてクリアされますがよろしいですか？"
        title=""
      />
    </>
  );
};
